import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  Progress,
  ErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useCampaigns } from '../../hooks/useCampaigns';
import { StateLabel } from './stateLabel';
import { CreateCampaignDialog } from './CreateCampaignDialog';

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
};

export const CampaignsListPage = () => {
  const { data, isFetching, error } = useCampaigns();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Page themeId="tool">
      <Header
        title="Campaigns"
        subtitle="Coordinated rollouts of upgrades, migrations, and standards"
      />
      <Content>
        <Box
          sx={theme => ({
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: theme.spacing(2),
          })}
        >
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New campaign
          </Button>
        </Box>
        <CreateCampaignDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
        {error && <ErrorPanel error={error as Error} />}
        {isFetching && !data && <Progress />}
        {data && data.length === 0 && (
          <Paper
            sx={theme => ({
              padding: theme.spacing(4),
              textAlign: 'center',
              color: theme.palette.text.secondary,
            })}
          >
            <Typography variant="h6">No campaigns yet</Typography>
            <Typography variant="body2">
              Create a campaign to coordinate an upgrade, migration, or
              standards push across the portfolio.
            </Typography>
          </Paper>
        )}
        {data && data.length > 0 && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell align="right">Checks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Link
                        component={RouterLink}
                        to={`/campaigns/${encodeURIComponent(c.id)}`}
                      >
                        {c.name || '(unnamed)'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StateLabel state={c.state} />
                    </TableCell>
                    <TableCell
                      sx={theme => ({ color: theme.palette.text.secondary })}
                    >
                      {c.ownerEntityRef || '—'}
                    </TableCell>
                    <TableCell
                      sx={theme => ({ color: theme.palette.text.secondary })}
                    >
                      {formatDate(c.startDate)}
                    </TableCell>
                    <TableCell
                      sx={theme => ({ color: theme.palette.text.secondary })}
                    >
                      {formatDate(c.targetDate)}
                    </TableCell>
                    <TableCell align="right">
                      {c.checks?.length ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Content>
    </Page>
  );
};
